import React from 'react';
import '../../css/StaticPages.css';

const TermsOfService = () => {
  React.useEffect(() => {
    document.documentElement.classList.add('allow-page-scroll');
    document.body.classList.add('allow-page-scroll');
    return () => {
      document.documentElement.classList.remove('allow-page-scroll');
      document.body.classList.remove('allow-page-scroll');
    };
  }, []);

  return (
    <div style={{ maxWidth: '900px', margin: '40px auto', padding: '0 16px', lineHeight: 1.75 }}>
      <h1 style={{ marginBottom: 8 }}>Terms of Service</h1>
      <p style={{ color: '#666', marginTop: 0 }}>Last updated: {new Date().toISOString().split('T')[0]}</p>

      <p>
        Welcome to our knowledge note management and sharing service ("Service"). By accessing or using the Service,
        you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you must not use
        the Service.
      </p>

      <h2>1. Account and Eligibility</h2>
      <ul>
        <li>You must be at least 16 years old or have parental consent to use the Service.</li>
        <li>You are responsible for maintaining the security of your account and password.</li>
        <li>You must provide accurate and complete information during registration.</li>
      </ul>

      <h2>2. User Content</h2>
      <ul>
        <li>
          "User Content" means any notes, documents, metadata, comments, tags, and other materials you upload, create,
          or share through the Service.
        </li>
        <li>You retain ownership of your User Content. We do not claim ownership.</li>
        <li>
          You grant us a limited, worldwide, non-exclusive license to host, process, and display your User Content
          solely for the purpose of providing and improving the Service.
        </li>
        <li>
          You are responsible for ensuring that your User Content complies with applicable laws and does not infringe
          third-party rights.
        </li>
      </ul>

      <h2>3. Sharing and Permissions</h2>
      <ul>
        <li>
          The Service allows you to share notes with collaborators or the public. You are responsible for the scope of
          access you grant and any consequences of sharing.
        </li>
        <li>
          Sharing links or invitations may be accessible by anyone who has the link, depending on your settings. Review
          your sharing settings carefully.
        </li>
        <li>You must not share content that is illegal, harmful, or violates others' rights.</li>
      </ul>

      <h2>4. Acceptable Use</h2>
      <ul>
        <li>No unlawful, harassing, defamatory, obscene, or infringing content.</li>
        <li>No attempts to probe, scan, or test the vulnerability of the Service.</li>
        <li>No reverse engineering, decompilation, or scraping without prior written consent.</li>
        <li>No spamming, malware distribution, or unauthorized automated access.</li>
      </ul>

      <h2>5. Privacy</h2>
      <p>
        Your use of the Service is also governed by our Privacy Policy, which explains how we collect, use, and protect
        your information. Please review it carefully.
      </p>

      <h2>6. Intellectual Property</h2>
      <ul>
        <li>The Service and its components are protected by copyright and other laws.</li>
        <li>
          Except for your User Content, all rights, title, and interest in and to the Service are owned by us and our
          licensors.
        </li>
      </ul>

      <h2>7. Subscriptions and Payments</h2>
      <h3>7.1 Paid Features</h3>
      <p>
        Certain features of the Service may require payment. Unless expressly stated otherwise in these Terms,
        all fees are denominated in U.S. dollars and are non-refundable to the maximum extent permitted by law.
        The prices and payment terms set forth in any applicable order form or checkout flow will govern in the
        event of any conflict with this Section 7.
      </p>

      <h3>7.2 Pricing</h3>
      <p>
        KBAI reserves the right to determine pricing for the Service. KBAI will use reasonable efforts to keep
        pricing information on the Service up to date. KBAI may change fees for any feature of the Service,
        including by adding new charges or fees. If you continue to use the Service after a price change takes
        effect, you agree to pay the updated amount. KBAI may, in its sole discretion, offer promotional discounts,
        features, or plans to certain customers. Unless such offers are made available to you, they do not apply to
        your plan or these Terms.
      </p>

      <h3>7.3 Payment Processing</h3>
      <p>
        To facilitate payments via bank account, credit card, or debit card, we use third-party payment processor
        Stripe, Inc. and its affiliates ("Stripe"). Payment processing services are provided by Stripe and are
        subject to Stripe's terms and policies available at <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer">https://stripe.com/legal</a> and
        Stripe's Global Privacy Policy available at <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a> (collectively, the "Stripe Agreement").
        By agreeing to these Terms and using the Service's payment features, you also agree to be bound by the
        Stripe Agreement, as it may be modified by Stripe from time to time. You authorize Stripe to store your
        designated payment method and to continue billing that method after its expiration to avoid interruptions in
        your use of the Service. Please contact Stripe for more information. KBAI disclaims any liability arising
        from payments you make via the Service.
      </p>

      <h3>7.4 Subscription Services</h3>
      <p>
        The Service may include subscription-based plans under which recurring fees are automatically billed ("Subscription Services").
        Your subscription billing date is the date on which you first purchase the Service and continues until you
        cancel or we terminate the Subscription Services. By activating a subscription, you authorize KBAI or its
        payment processors to charge all accrued amounts on a recurring basis until cancellation, on or before each
        payment due date. Your account will be charged applicable fees and taxes on the subscription billing date and
        on each renewal date thereafter. You must cancel at least 24 hours before renewal to avoid charges for the
        next billing period. KBAI or its payment processors will charge the periodic subscription fees to the payment
        method associated with your account or any other method you provide.
      </p>

      <h3>7.5 Delinquent Accounts</h3>
      <p>
        For any account with overdue amounts, KBAI may suspend or terminate access to the Service (including paid
        features). In addition to the outstanding amounts, delinquent accounts are responsible for any fees associated
        with refund processing or the collection of unpaid amounts, including reasonable collection costs. If your
        payment method is no longer valid at the time renewal fees come due, KBAI reserves the right to delete your
        account and any associated information without liability to you.
      </p>

      <h2>8. Data Storage and Availability</h2>
      <ul>
        <li>
          We strive for high availability but do not guarantee uninterrupted access. Back up your critical content
          regularly.
        </li>
        <li>
          We may impose reasonable limits on storage, bandwidth, or API usage to protect the Service and its users.
        </li>
      </ul>

      <h2>9. Termination</h2>
      <ul>
        <li>We may suspend or terminate your access for violations of these Terms or suspected abuse.</li>
        <li>You may stop using the Service at any time. Upon termination, your access will cease.</li>
        <li>We may retain backups for a limited period as required by law or for legitimate business purposes.</li>
      </ul>

      <h2>10. Disclaimers</h2>
      <p>
        The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, express or
        implied. We do not warrant that the Service will be error-free, secure, or uninterrupted.
      </p>

      <h2>11. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, we will not be liable for indirect, incidental, special, consequential,
        or punitive damages, or any loss of profits, data, or goodwill arising from your use of the Service.
      </p>

      <h2>12. Changes to the Service or Terms</h2>
      <ul>
        <li>We may modify the Service or these Terms at any time with reasonable notice.</li>
        <li>Your continued use after changes indicates your acceptance of the updated Terms.</li>
      </ul>

      <h2>13. Contact</h2>
      <p>
        If you have questions about these Terms, please contact us via the support channels provided in the Service.
      </p>
    </div>
  );
};

export default TermsOfService;


